import React, { useState, useEffect } from 'react';
import { Bell, Plus, Trash2, Users, GraduationCap, AlertCircle, Send } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';

const AUDIENCE_OPTIONS = [
  { key: 'teachers', label: 'Teachers', icon: GraduationCap },
  { key: 'parents', label: 'Parents', icon: Users },
];

const PRIORITY_STYLES = {
  normal: 'bg-blue-50 text-blue-700 border-blue-100',
  urgent: 'bg-red-50 text-red-700 border-red-100',
};

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function AnnouncementsPage() {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [audience, setAudience] = useState(['teachers', 'parents']);
  const [priority, setPriority] = useState('normal');
  const [sending, setSending] = useState(false);

  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      const d = await api.get('/announcements');
      if (d.success) setAnnouncements(d.data);
    } catch { setAnnouncements([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAnnouncements(); }, []);

  const toggleAudience = (key) => {
    setAudience(prev => prev.includes(key) ? prev.filter(a => a !== key) : [...prev, key]);
  };

  const handleSend = async () => {
    if (!title.trim() || !content.trim()) {
      toast.error('Title and message are required');
      return;
    }
    if (audience.length === 0) {
      toast.error('Select at least one audience');
      return;
    }
    setSending(true);
    try {
      await api.post('/announcements', { title, content, audience, priority });
      toast.success('Announcement published');
      setTitle('');
      setContent('');
      setAudience(['teachers', 'parents']);
      setPriority('normal');
      fetchAnnouncements();
    } catch (err) {
      toast.error(err?.detail || 'Failed to publish');
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/announcements/${id}`);
      toast.success('Deleted');
      setAnnouncements(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      toast.error(err?.detail || 'Failed to delete');
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="announcements-page">
      <div className="mb-5">
        <h1 className="text-xl font-bold text-slate-900">Announcements</h1>
        <p className="text-sm text-slate-500 mt-0.5">Broadcast messages to teachers and parents</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* Compose Form */}
        <div className="lg:col-span-2">
          <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4 sticky top-6">
            <h2 className="font-semibold text-slate-800 flex items-center gap-2">
              <Plus className="w-4 h-4 text-indigo-500" />
              New Announcement
            </h2>

            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">Title *</label>
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="e.g. Parent-Teacher Meeting"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                data-testid="announcement-title-input"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">Message *</label>
              <textarea
                value={content}
                onChange={e => setContent(e.target.value)}
                rows={4}
                placeholder="Type your announcement here..."
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none resize-none"
                data-testid="announcement-content-input"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600 mb-2 block">Audience *</label>
              <div className="flex flex-wrap gap-2">
                {AUDIENCE_OPTIONS.map(({ key, label, icon: Icon }) => (
                  <button
                    key={key}
                    onClick={() => toggleAudience(key)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-all ${
                      audience.includes(key)
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'
                    }`}
                    data-testid={`audience-${key}`}
                  >
                    <Icon className="w-3 h-3" />
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">Priority</label>
              <div className="flex gap-2">
                {['normal', 'urgent'].map(p => (
                  <button
                    key={p}
                    onClick={() => setPriority(p)}
                    className={`flex-1 py-1.5 rounded-lg border text-xs font-medium transition-all capitalize ${
                      priority === p
                        ? p === 'urgent' ? 'bg-red-600 text-white border-red-600' : 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`priority-${p}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={handleSend}
              disabled={sending || !title.trim() || !content.trim()}
              className="w-full bg-indigo-600 text-white text-sm py-2.5 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="send-announcement-btn"
            >
              <Send className="w-4 h-4" />
              {sending ? 'Publishing…' : 'Publish Announcement'}
            </button>
          </div>
        </div>

        {/* Announcements List */}
        <div className="lg:col-span-3 space-y-3">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="w-7 h-7 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : announcements.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
              <Bell className="w-10 h-10 text-slate-200 mx-auto mb-3" />
              <p className="font-medium text-slate-500">No announcements yet</p>
              <p className="text-sm text-slate-400 mt-1">Create your first announcement to notify staff and parents</p>
            </div>
          ) : (
            announcements.map(ann => (
              <div
                key={ann.id}
                className={`bg-white border rounded-xl p-4 ${ann.priority === 'urgent' ? 'border-red-200' : 'border-slate-200'}`}
                data-testid={`announcement-${ann.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      {ann.priority === 'urgent' && (
                        <span className="bg-red-100 text-red-700 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide flex items-center gap-1">
                          <AlertCircle className="w-2.5 h-2.5" /> Urgent
                        </span>
                      )}
                      <span className="text-xs text-slate-400">{timeAgo(ann.created_at)}</span>
                    </div>
                    <h3 className="font-semibold text-slate-800">{ann.title}</h3>
                    <p className="text-sm text-slate-600 mt-1 whitespace-pre-line">{ann.content}</p>
                    <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                      {ann.audience?.map(a => (
                        <span key={a} className="bg-slate-100 text-slate-500 text-[10px] px-2 py-0.5 rounded-full capitalize">{a}</span>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(ann.id)}
                    className="text-slate-300 hover:text-red-500 transition-colors p-1.5 rounded-lg hover:bg-red-50 flex-shrink-0"
                    data-testid={`delete-ann-${ann.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
